import { components, SORT_ORDER } from '@common/types';
import { toNumber } from 'lodash';
import { Dayjs } from 'dayjs';
import { usePatientDocumentDownloadMutation } from '@common/cache';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';

export type CategoryType =
  components['schemas']['ShortPatientDocumentsResponse']['category'] | '';

export type DocumentsColumnNames =
  'file_name' | 'size' | 'category' | 'uploaded_by' | 'uploaded_on';

export interface IDocumentsTableEntries {
  id: string;
  file_name: string;
  file_format: string;
  size: string;
  category: CategoryType;
  specification: string | null;
  uploaded_by: {
    name: string;
    role: string;
  };
  uploaded_on: Dayjs;
}

export interface IDocumentsTableFilters {
  sort_by: DocumentsColumnNames;
  sort_order: SORT_ORDER;
  page: number;
  limit: number;
  category: CategoryType;
  search?: string;
}

interface IUseDocumentsTableProps {
  filters: IDocumentsTableFilters;
  setFilters: (filters: IDocumentsTableFilters) => void;
}

export const useDocumentsTable = ({
  filters,
  setFilters,
}: IUseDocumentsTableProps) => {
  const { sort_by, sort_order } = filters;
  const fileNameRef = useRef<string>('');

  const { data, isPending, mutateAsync } = usePatientDocumentDownloadMutation();

  useEffect(() => {
    if (data) {
      const url = URL.createObjectURL(data);

      const a = document.createElement('a');
      a.href = url;
      a.download = fileNameRef.current;
      fileNameRef.current = '';

      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
        }, 150);
      }

      a.addEventListener('click', handleOnDownload, false);
      a.click();
    }
  }, [data]);

  const handleDownload = async (document_id: string, file_name: string) => {
    const loadingToast = toast.loading('Downloading  file...', {
      isLoading: true,
      autoClose: 500,
    });
    fileNameRef.current = file_name;

    try {
      await mutateAsync(document_id);
      toast.success('Downloaded');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  const handleChangeOrder = (colName: DocumentsColumnNames) => {
    setFilters({
      ...filters,
      sort_by: colName,
      sort_order: filters.sort_order === 'asc' ? 'desc' : 'asc',
    });
  };

  const onPageIndexChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setFilters({ ...filters, page: page + 1 });
  };

  const onPageSizeChange = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({ ...filters, limit: toNumber(event.target.value), page: 1 });
  };

  return {
    sort_by,
    sort_order,
    onPageIndexChange,
    onPageSizeChange,
    handleChangeOrder,
    handleDownload,
    isDownloading: isPending,
  };
};
